import { setSentryContextUserInfoClear } from '@simppl/react-sentry';

import axios from '..';
import type { GeyMyReadSubjectReviewSummaryResType } from '../subjectReview/res';
import LoginStorage from './LoginStorage';
import type {
  DeleteUserReqType,
  GetAdmissionsAvailableCountsReqType,
  GetAllUserPointHistoryReqType,
  GetMyPointHistoryReqType,
  GetMyReadSubjectReviewsReqType,
  GetMyWriteSubjectReviewsReqType,
  GetUserByIdReqType,
  GetUserEduInfoReqType,
  GetUserPointHistoryReqType,
  GetUserPointReqType,
  GetUserProfileInfoReqType,
  GetUsersIdListReqType,
  GetUsersUserIdCommunityCommentsReqType,
  GetUsersUserIdCommunityPostsReqType,
  GetUsersValidateUserReqType,
  PostCheckUserPasswordReqType,
  PostUserPointReqType,
  PostUsersCheckAuthCodeReqType,
  PostUsersDuplicateEmailReqType,
  PostUsersDuplicateNicknameReqType,
  PostUsersLoginReqType,
  PostUsersPasswordSetupCheckAuthCodeReqType,
  PostUsersPasswordSetupReqType,
  PostUsersReqType,
  PostUsersSendEmailReqType,
  PostUsersSocialRegisterDuplicateEmailReqType,
  PutUserInfoReqType,
  PutUsersPasswordReqType,
} from './req';
import type {
  DeleteUserResType,
  GetAdmissionsAvailableCountsResType,
  GetAllUserPointHistoryResType,
  GetMyPointHistoryResType,
  GetMyReadSubjectReviewsResType,
  GetMyWriteSubjectReviewsResType,
  GetUserEduInfoResType,
  GetUserPointHistoryResType,
  GetUserPointResType,
  GetUserProfileInfoResType,
  GetUserResType,
  GetUsersIdListResType,
  GetUsersPaymentHistoryResType,
  GetUsersTokenUserInfoResType,
  GetUsersUserIdCommunityCommentsResType,
  GetUsersUserIdCommunityPostsResType,
  GetUsersValidateUserResType,
  PostCheckUserPasswordResType,
  PostUserPointResType,
  PostUsersCheckAuthCodeResType,
  PostUsersDuplicateEmailResType,
  PostUsersDuplicateNicknameResType,
  PostUsersIssueTokenResType,
  PostUsersLoginResType,
  PostUsersLogoutResType,
  PostUsersPasswordSetupCheckAuthCodeResType,
  PostUsersPasswordSetupResType,
  PostUsersPasswordSetupSendEmailResType,
  PostUsersResType,
  PostUsersSendEmailResType,
  PostUsersSocialRegisterDuplicateEmailResType,
  PutUserInfoResType,
  PutUsersPasswordResType,
} from './res';

export { userAdmissions, userAdmissionsId, userInterestAdmissions } from './admissions';

export const postUsersDuplicateEmail = (body: PostUsersDuplicateEmailReqType) =>
  axios
    .post<PostUsersDuplicateEmailResType>(`/users/duplicate-email`, body)
    .then((res) => res.data);

export const postUsersSocialRegisterDuplicateEmail = (
  body: PostUsersSocialRegisterDuplicateEmailReqType,
) =>
  axios
    .post<PostUsersSocialRegisterDuplicateEmailResType>(
      '/users/social/register/duplicate-email',
      body,
    )
    .then((res) => res.data);

export const postUsersSendEmail = (body: PostUsersSendEmailReqType) =>
  axios.post<PostUsersSendEmailResType>(`/users/send-email`, body).then((res) => res.data);

export const postUsersCheckAuthCode = (body?: PostUsersCheckAuthCodeReqType) =>
  axios.post<PostUsersCheckAuthCodeResType>('/users/check-auth-code', body);

export const postUsersPasswordSetupSendEmail = () =>
  axios
    .post<PostUsersPasswordSetupSendEmailResType>('/users/password/setup/send-email')
    .then((res) => res.data);

export const postUsersPasswordSetupCheckAuthCode = (
  body: PostUsersPasswordSetupCheckAuthCodeReqType,
) =>
  axios
    .post<PostUsersPasswordSetupCheckAuthCodeResType>('/users/password/setup/check-auth-code', body)
    .then((res) => res.data);

export const postUsersPasswordSetup = (body: PostUsersPasswordSetupReqType) =>
  axios.post<PostUsersPasswordSetupResType>('/users/password/setup', body).then((res) => res.data);

export const postUsers = (body: PostUsersReqType) =>
  axios.post<PostUsersResType>('/users', body).then((res) => res.data);

export const deleteUser = ({ userId, reason }: DeleteUserReqType) =>
  axios.delete<DeleteUserResType>(`/users/${userId}`, { data: { reason } }).then((res) => res.data);

export const getUserInfo = () =>
  axios.get<GetUsersTokenUserInfoResType>(`/users/token-user-info`).then((res) => res.data);

export const getUserById = ({ userId }: GetUserByIdReqType) =>
  axios.get<GetUserResType>(`/users/${userId}`).then((res) => res.data);

export const getUserEduInfo = ({ userId }: GetUserEduInfoReqType) =>
  axios.get<GetUserEduInfoResType>(`/users/${userId}/edu-info`).then((res) => res.data);

export const putUserInfo = (body: PutUserInfoReqType) =>
  axios.put<PutUserInfoResType>('/users/info', body).then((res) => res.data);

export const getUserProfileInfo = ({ userId }: GetUserProfileInfoReqType) =>
  axios
    .get<GetUserProfileInfoResType>(`/users/${userId}/community-profile`)
    .then((res) => res.data);

export const postUsersLogin = ({ email, password, isAdmin = false }: PostUsersLoginReqType) =>
  axios.post<PostUsersLoginResType>('/users/login', { email, password, isAdmin }).then((res) => {
    const { token, refreshToken } = res.data.data;
    LoginStorage.setToken({ value: token, type: 'access' });
    LoginStorage.setToken({ value: refreshToken, type: 'refresh' });
    return res;
  });

export const postUsersIssueToken = () =>
  axios
    .post<PostUsersIssueTokenResType>('/users/issue-token', {
      refreshToken: LoginStorage.getToken({ type: 'refresh' }),
    })
    .then((res) => {
      const { token, refreshToken } = res.data.data;
      LoginStorage.setToken({ value: token, type: 'access' });
      LoginStorage.setToken({ value: refreshToken, type: 'refresh' });
      setSentryContextUserInfoClear();
      return res;
    });

export const postUsersLogout = () =>
  axios
    .post<PostUsersLogoutResType>('/users/logout')
    .catch(() => {
      return null;
    })
    .finally(() => {
      const isWebview = JSON.stringify(window.ReactNativeWebView) === '{}';
      LoginStorage.removeToken({ type: 'access' });
      LoginStorage.removeToken({ type: 'refresh' });
      if (!isWebview) LoginStorage.setAutoLoginState(false);
      localStorage.removeItem('simppl-select-university-id');
      window.location.reload();
    });

export const getUsersPaymentHistory = () =>
  axios.get<GetUsersPaymentHistoryResType>('/users/payment-history').then((res) => res.data);

export const getUsersIdList = (params: GetUsersIdListReqType) =>
  axios.get<GetUsersIdListResType>(`/users/id-list`, { params }).then((res) => res.data);

export const putUsersPassword = (body: PutUsersPasswordReqType) =>
  axios.put<PutUsersPasswordResType>('/users/password', body);

export const getUsersValidateUser = (params: GetUsersValidateUserReqType) =>
  axios.get<GetUsersValidateUserResType>('/users/validate-user', { params });

export const postUsersDuplicateNickname = (body: PostUsersDuplicateNicknameReqType) =>
  axios.post<PostUsersDuplicateNicknameResType>('users/duplicate-nickname', body);

export const getAdmissionsAvailableCounts = ({ userId }: GetAdmissionsAvailableCountsReqType) =>
  axios
    .get<GetAdmissionsAvailableCountsResType>(`/users/${userId}/payment-histories/total-count`)
    .then((res) => res.data);

export const getUsersUserIdCommunityPosts = ({
  userId,
  ...params
}: GetUsersUserIdCommunityPostsReqType) =>
  axios
    .get<GetUsersUserIdCommunityPostsResType>(`/users/${userId}/community-posts`, { params })
    .then((res) => res.data);

export const getUsersUserIdCommunityComments = ({
  userId,
  ...params
}: GetUsersUserIdCommunityCommentsReqType) =>
  axios
    .get<GetUsersUserIdCommunityCommentsResType>(`/users/${userId}/community-comments`, { params })
    .then((res) => res.data);

export const postCheckUserPassword = ({ password }: PostCheckUserPasswordReqType) =>
  axios
    .post<PostCheckUserPasswordResType>(`/users/password/check`, { password })
    .then((res) => res.data);

export const getMyWriteSubjectReviews = ({ userId, ...params }: GetMyWriteSubjectReviewsReqType) =>
  axios
    .get<GetMyWriteSubjectReviewsResType>(`/users/${userId}/subject-reviews/write`, {
      params,
    })
    .then((res) => res.data);

export const getMyReadSubjectReviews = ({ userId, ...params }: GetMyReadSubjectReviewsReqType) =>
  axios
    .get<GeyMyReadSubjectReviewSummaryResType>(`/users/${userId}/subject-reviews/read`, {
      params,
    })
    .then((res) => res.data);

export const getMyPointHistories = ({ userId, ...params }: GetMyPointHistoryReqType) =>
  axios
    .get<GetMyPointHistoryResType>(`/users/${userId}/point-history`, { params })
    .then((res) => res.data);

export const getUserPointHistories = ({ userId, ...params }: GetUserPointHistoryReqType) =>
  axios
    .get<GetUserPointHistoryResType>(`/admins/users/${userId}/point-history`, { params })
    .then((res) => res.data);

export const getUserPoint = ({ userId }: GetUserPointReqType) =>
  axios.get<GetUserPointResType>(`/admins/users/${userId}/point`).then((res) => res.data);

/**
 * 사용 주의!!!
 * ADMIN 전용 !!!!!
 * 사용자 포인트 조절 API 호출
 */
export const postUserPoint = ({ userId, ...params }: PostUserPointReqType) =>
  axios.post<PostUserPointResType>(`/admins/users/${userId}/point`, params).then((res) => res.data);

export const getAllUserPointHistories = (params: GetAllUserPointHistoryReqType) =>
  axios
    .get<GetAllUserPointHistoryResType>(`/admins/users/point-histories`, { params })
    .then((res) => res.data);
